import Section from "@intility/bifrost-react/Section";
import { transformer } from "@openfga/syntax-transformer";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { getModel } from "~/api/client";
import type { AuthorizationModel } from "~/api/fga-types";
import { PageLayout } from "~/components/layout/PageLayout";
import ModelDetails from "~/components/model/ModelDetails";

export default function AuthorizationModelDetail() {
  const { modelId } = useParams<{ modelId: string }>();
  const [model, setModel] = useState<AuthorizationModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!modelId) return;
    getModel(modelId)
      .then((res) => setModel(res))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [modelId]);

  const dsl = useMemo(() => {
    if (!model) return null;
    try {
      // The transformer expects OpenFGA snake_case format
      const fgaModel = {
        schema_version: model.schemaVersion,
        type_definitions: model.typeDefinitions,
      };
      return transformer.transformJSONToDSL(
        fgaModel as Parameters<typeof transformer.transformJSONToDSL>[0],
      );
    } catch {
      return null;
    }
  }, [model]);

  if (loading) return <p className="bf-p">Loading model...</p>;
  if (error) return <p className="bf-p bfc-alert">Error: {error}</p>;
  if (!model) return <p className="bf-p">Model not found</p>;

  return (
    <PageLayout
      breadcrumbs={[
        { label: "Home", to: "/" },
        { label: "Authorization" },
        { label: "Model", to: "/authorization/model" },
        { label: "Version" },
      ]}
      title="Authorization Model"
      level={2}
      description={
        <>
          ID: <code>{model.id}</code> &middot; Schema: {model.schemaVersion}
        </>
      }
    >
      {dsl && (
        <Section>
          <Section.Header>DSL</Section.Header>
          <pre
            style={{
              margin: 0,
              padding: 16,
              fontFamily: "monospace",
              fontSize: 13,
              lineHeight: 1.5,
              whiteSpace: "pre",
              overflowX: "auto",
              backgroundColor: "var(--bf-base-2-bg)",
              color: "var(--bf-base-color)",
            }}
          >
            {dsl}
          </pre>
        </Section>
      )}

      <ModelDetails typeDefinitions={model.typeDefinitions ?? []} />
    </PageLayout>
  );
}
